/**
 * useUnreadCount
 * Polls the unread message count every 30 seconds.
 * Used by Header to show the unread badge on the chat icon.
 *
 * Only polls when the user is authenticated.
 * Failures are silent (badge just stays at last known value).
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuthContext } from '../../../contexts/AuthContext';
import { chatService } from '../services/chatService';

const POLL_INTERVAL_MS = 30_000;

/**
 * @returns {{
 *   totalUnread: number,
 *   byConversation: Object<string, number>
 * }}
 */
export default function useUnreadCount() {
  const [totalUnread, setTotalUnread] = useState(0);
  const [byConversation, setByConversation] = useState({});

  const { isAuthenticated } = useAuthContext();

  const fetchUnreadCount = useCallback(async () => {
    // Guard: only run when logged in
    if (!isAuthenticated || !isAuthenticated()) return;
    try {
      const data = await chatService.getUnreadCount();
      setTotalUnread(data.total ?? 0);
      setByConversation(data.byConversation ?? {});
    } catch {
      // Silent — badge stays at last known value
    }
  }, [isAuthenticated]);

  useEffect(() => {
    fetchUnreadCount();
    const intervalId = setInterval(fetchUnreadCount, POLL_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [fetchUnreadCount]);

  return { totalUnread, byConversation };
}
